import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, createCommentVNode as _createCommentVNode, KeepAlive as _KeepAlive, withCtx as _withCtx, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode } from "vue"

import { computed, ref } from "vue";
import { humanSize, ESTIMATED_AVAILABLE_SIZE } from "@/keyboard";
import { useKeyboardStore } from "@/stores";
import KeysLayout from "@/components/utils/KeysLayout.vue";
import KeyboardButton from "@/components/keys/KeyboardButton.vue";
import KeyCap from "@/components/keys/KeyCap.vue";
import ActionSearch from "@/components/keys/ActionSearch.vue";
import QuickToolButton from "@/components/keys/QuickToolButton.vue";
import PresetSelect from "@/components/keys/PresetSelect.vue";

type KeyCapT = InstanceType<typeof KeyCap>;

export default /*@__PURE__*/_defineComponent({
  __name: 'KeyboardArea',
  setup(__props) {

const buttons = ref<InstanceType<typeof KeyboardButton>[]>([]);

const kb = useKeyboardStore();

const sizeHover = ref(false);
const sizeInfo = computed(() => {
  const used = humanSize(kb.getEstimatedConfigSize());
  const available = humanSize(ESTIMATED_AVAILABLE_SIZE);
  const percent = (used.bytes / available.bytes) * 100;
  return {
    used,
    available,
    percent,
    exceeded: used.bytes >= available.bytes,
  };
});

const actionHighlight = ref(false);

// Drag & drop
const dragged = ref<KeyCapT | null>(null);
const draggedOver = ref<KeyCapT | null>(null);

const dragStart = (e: DragEvent, kc: KeyCapT) => {
  if (e.dataTransfer == null) return;
  dragged.value = kc;
  e.dataTransfer.dropEffect = "move";
  e.dataTransfer.setData("text/plain", "");
};

const dragEnter = (e: DragEvent, kc: KeyCapT) => {
  if (e.dataTransfer == null || dragged.value == null) return;
  e.dataTransfer.dropEffect = "move";

  const isDragStart =
    kc.$props.row == dragged.value.$props.row &&
    kc.$props.col == dragged.value.$props.col;
  if (!isDragStart) {
    // Clear 'dragover' on all buttons to prevent leaving some buttons
    // with 'dragover' when dragenter/dragleave happen in unexpected order.
    buttons.value.forEach((b) => {
      b.$refs.keycap.$el.classList.remove("dragover");
    });
    draggedOver.value = kc;
    kc.$el.classList.add("dragover");
  }
};

const dragLeave = (e: DragEvent, kc: KeyCapT) => {
  const isSame =
    draggedOver.value != null &&
    kc.$props.row == draggedOver.value.$props.row &&
    kc.$props.col == draggedOver.value.$props.col;

  if (draggedOver.value == null || !isSame) {
    kc.$el.classList.remove("dragover");
  }

  draggedOver.value = null;
};

const dragEnd = (_e: DragEvent, _kc: KeyCapT) => {
  buttons.value.forEach((b) => {
    b.$refs.keycap.$el.classList.remove("dragover");
  });
};

// const swapDurationMs = 300;
const drop = (e: DragEvent, kc: KeyCapT) => {
  if (dragged.value == null) return;

  // Animate swapping by temporarily adding translate() styles that move
  // to the destination along with "swapping" class for smooth transition.
  // TODO: handle rotation difference
  const box = (elem: HTMLElement) => elem.getBoundingClientRect();
  const from = dragged.value.$el;
  const to = kc.$el;
  const dx = box(to).left - box(from).left;
  const dy = box(to).top - box(from).top;
  const drot = kc.$props.rot - dragged.value.$props.rot;

  from.classList.add("dnd-swapping");
  to.classList.add("dnd-swapping");
  from.style.transform = `translate(${dx}px, ${dy}px) rotate(${-drot}deg)`;
  to.style.transform = `translate(${-dx}px, ${-dy}px) rotate(${drot}deg)`;

  setTimeout(() => {
    // HACK: Stop transition animation to avoid keys "going back" to their positions
    // This is needed to override defaults animation settings for KeyCap
    from.classList.add("dnd-no-transition");
    to.classList.add("dnd-no-transition");

    // Move keys back to where they were
    from.style.transform = "";
    to.style.transform = "";

    // Revert back to normal transition settings as soon as possible
    setTimeout(() => {
      from.classList.remove("dnd-swapping");
      to.classList.remove("dnd-swapping");
      from.classList.remove("dnd-no-transition");
      to.classList.remove("dnd-no-transition");
    }, 300);

    // Finally perform the state change
    if (dragged.value == null) return;
    const dragRow = dragged.value.$props.row;
    const dragCol = dragged.value.$props.col;
    const [dropRow, dropCol] = [kc.$props.row, kc.$props.col];

    const dragAct = kb.getAction(dragRow, dragCol);
    const dropAct = kb.getAction(dropRow, dropCol);
    kb.setAction(dragRow, dragCol, dropAct);
    kb.setAction(dropRow, dropCol, dragAct);
  }, 300 /* swapDurationMs */);
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createBlock(KeysLayout, null, {
    default: _withCtx((s) => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(s.keys, (k, id) => {
        return (_openBlock(), _createBlock(KeyboardButton, {
          key: id,
          ref_for: true,
          ref_key: "buttons",
          ref: buttons,
          row: k.row,
          col: k.col,
          x: k.key.x,
          y: k.key.y,
          rot: k.key.rot,
          height: k.key.size.y,
          width: k.key.size.x,
          onDragstart: dragStart,
          onDragenter: dragEnter,
          onDragleave: dragLeave,
          onDragend: dragEnd,
          onDrop: drop
        }, null, 8, ["row", "col", "x", "y", "rot", "height", "width"]))
      }), 128)),
      _createElementVNode("div", {
        class: _normalizeClass(["absolute bottom-3 left-3 flex justify-center items-center gap-2 hide-on-action", actionHighlight.value ? 'hide' : ''])
      }, [
        _createVNode(QuickToolButton, { tooltip: "Search for keys with specific actions" }, {
          icon: _withCtx(() => [
            _createVNode(_component_Icon, {
              icon: "material-symbols:search-rounded",
              class: "text-lg m-auto"
            })
          ]),
          popup: _withCtx((slotProps) => [
            (_openBlock(), _createBlock(_KeepAlive, null, [
              (slotProps.visible)
                ? (_openBlock(), _createBlock(ActionSearch, {
                    key: 0,
                    onSearchChanged: () => slotProps.forceUpdate(),
                    onHighlightChanged: _cache[0] || (_cache[0] = (on) => (actionHighlight.value = on))
                  }, null, 8, ["onSearchChanged"]))
                : _createCommentVNode("", true)
            ], 1024))
          ]),
          _: 1
        }),
        _createVNode(QuickToolButton, { tooltip: "Quickly apply keys preset" }, {
          icon: _withCtx(() => [
            _createVNode(_component_Icon, {
              icon: "material-symbols:menu-book-outline",
              class: "text-lg m-auto"
            })
          ]),
          popup: _withCtx(() => [
            _createVNode(PresetSelect)
          ]),
          _: 1
        })
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass(["absolute bottom-3 right-3 tooltip", sizeInfo.value.exceeded ? 'tooltip-error' : '']),
        "data-tip": "Estimated configuration memory usage (may be inaccurate). If too much space is used then firmware compilation will fail.",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (sizeHover.value = !sizeHover.value))
      }, [
        _createElementVNode("span", {
          class: _normalizeClass(["transition-all btn btn-ghost btn-sm normal-case", sizeInfo.value.exceeded ? 'animate-pulse font-bold text-error' : ''])
        }, [
          (sizeHover.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode(_toDisplayString(sizeInfo.value.used.value.toFixed(1)) + " " + _toDisplayString(sizeInfo.value.used.prefix + "B") + " / " + _toDisplayString(sizeInfo.value.available.value.toFixed(1)) + " " + _toDisplayString(sizeInfo.value.available.prefix + "B"), 1)
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createTextVNode(_toDisplayString(sizeInfo.value.percent.toFixed(0)) + "% ", 1)
              ], 64))
        ], 2)
      ], 2)
    ]),
    _: 1
  }))
}
}

})